import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';
import { useToast } from '@chakra-ui/react';
import CryptoJS from 'crypto-js';
import env from 'config/env';

interface MediaAttachment {
  id: string;
  journal_entry_id: string;
  media_type: 'image' | 'audio' | 'document';
  encrypted_filename: string;
  mime_type: string;
  file_size: number;
  thumbnail_url?: string;
  created_at: string;
}

interface MediaContextType {
  uploadMedia: (file: File, journalEntryId: string) => Promise<MediaAttachment>;
  deleteMedia: (id: string) => Promise<void>;
  getMediaForEntry: (journalEntryId: string) => Promise<MediaAttachment[]>;
  decryptFilename: (encryptedFilename: string) => string;
}

const MediaContext = createContext<MediaContextType | undefined>(undefined);

export function MediaProvider({ children }: { children: React.ReactNode }) {
  const toast = useToast();

  const encryptFile = async (file: File): Promise<{ encryptedData: string; encryptedFilename: string }> => {
    const masterKey = localStorage.getItem('masterKey');
    if (!masterKey) {
      throw new Error('No master key found');
    }

    // Encrypt filename
    const encryptedFilename = CryptoJS.AES.encrypt(file.name, masterKey).toString();

    // Read file as array buffer
    const arrayBuffer = await file.arrayBuffer();
    const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
    
    // Encrypt file data
    const encryptedData = CryptoJS.AES.encrypt(wordArray, masterKey).toString();

    return { encryptedData, encryptedFilename };
  };

  const decryptFilename = (encryptedFilename: string): string => {
    try {
      const masterKey = localStorage.getItem('masterKey');
      if (!masterKey) return '[Encrypted File]';

      const bytes = CryptoJS.AES.decrypt(encryptedFilename, masterKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Error decrypting filename:', error);
      return '[Encrypted File]';
    }
  };

  const uploadMedia = async (file: File, journalEntryId: string): Promise<MediaAttachment> => {
    try {
      const { encryptedData, encryptedFilename } = await encryptFile(file);

      const formData = new FormData();
      formData.append('encrypted_data', encryptedData);
      formData.append('encrypted_filename', encryptedFilename);
      formData.append('mime_type', file.type);
      formData.append('file_size', file.size.toString());
      formData.append('journal_entry_id', journalEntryId);

      const response = await axios.post(`${env.API_URL}/media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      toast({
        title: 'Error uploading file',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const deleteMedia = async (id: string): Promise<void> => {
    try {
      await axios.delete(`${env.API_URL}/media/${id}`);
    } catch (error) {
      toast({
        title: 'Error deleting file',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  const getMediaForEntry = async (journalEntryId: string): Promise<MediaAttachment[]> => {
    try {
      const response = await axios.get(`${env.API_URL}/media/entry/${journalEntryId}`);
      return response.data;
    } catch (error) {
      toast({
        title: 'Error fetching media',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  };

  return (
    <MediaContext.Provider
      value={{
        uploadMedia,
        deleteMedia,
        getMediaForEntry,
        decryptFilename,
      }}
    >
      {children}
    </MediaContext.Provider>
  );
}

export function useMedia() {
  const context = useContext(MediaContext);
  if (context === undefined) {
    throw new Error('useMedia must be used within a MediaProvider');
  }
  return context;
} 